/**
 * VAD (Voice Activity Detection) do Modo de Voz — RMS com ataque instantâneo
 * / liberação com histerese, mesma matemática já validada ao vivo em
 * `fee-fixo/.../grafo-de-audio.js` (não importada de lá: módulo duplicado por
 * decisão, mesmo padrão do resto deste módulo — ver o cabeçalho de
 * `captura-de-voz.js`).
 *
 * Extraído de `motor-de-voz.js` (voz v3, 08/09) — o arquivo original passou
 * do teto de 500 linhas ao ganhar o barge-in. Puro/sem mic aqui: as duas
 * factories só recebem `rms` e chamam o callback combinado — quem pede o
 * microfone e decide o que fazer no callback é `motor-de-voz.js`.
 *
 * Duas réguas, pelo MESMO objetivo — "nunca mandar silêncio pro STT nem
 * alucinar em cima do próprio áudio da IA" ([feedback do owner, 08/09]):
 *
 * 1. `criarVadDeAutoEnvio` — o de sempre (auto-envio do Modo de Voz), agora
 *    com um MÍNIMO de fala sustentada (`MIN_FALA_MS`) antes de armar o
 *    "falando": um ruído breve (respiração, clique) não pode mais ligar o
 *    release. `MIN_CLIPE_MS` é o segundo backstop, aplicado em
 *    `motor-de-voz.js` sobre a duração REAL do clipe gravado (não cabe aqui —
 *    esta função só vê RMS, não o `Blob`).
 * 2. `criarVadDeBargeIn` — NOVO: um limiar mais alto (o dobro do de
 *    auto-envio) e uma histerese de ATAQUE (não de liberação) — dispara
 *    quando a fala do usuário se sustenta por `HISTERESE_BARGE_IN_MS`
 *    enquanto a IA está falando. ⚠️ Cuidado com eco: mesmo com
 *    `echoCancellation` (`captura-de-voz.js`), algum vazamento do áudio da
 *    IA pode chegar ao mic — por isso o limiar é MAIOR que o de fala normal,
 *    não o mesmo. Trade-off aceito, não escondido: em ambiente muito
 *    reverberante ainda é POSSÍVEL o próprio áudio da IA soar como barge-in;
 *    a defesa de 2ª linha é `MIN_CLIPE_MS` no lado de quem grava o turno
 *    novo, que descarta clipes curtos demais pra ser fala de verdade.
 */

export const LIMIAR_VAD_RMS = 0.02
export const RELEASE_VAD_MS = 1000

// [V1, voz v3] Fala sustentada mínima antes do release do auto-envio poder
// disparar — abaixo disso, o release nunca arma (`_falouOSuficiente` nunca
// vira `true`), e o mic continua ouvindo.
export const MIN_FALA_MS = 400

// [V1, voz v3] Duração mínima do CLIPE gravado (não do RMS) — segunda
// checagem, sobre `duracaoSegundos` do `GravadorDeTurno`, feita em
// `motor-de-voz.js` antes de chamar o STT.
export const MIN_CLIPE_MS = 800

// [V2, voz v3] Barge-in: limiar mais alto que o de auto-envio — ver o
// cabeçalho acima sobre o trade-off do eco.
export const LIMIAR_BARGE_IN_RMS = LIMIAR_VAD_RMS * 2
export const HISTERESE_BARGE_IN_MS = 300

/**
 * ★ [spec-008 T079] Quanto tempo de QUEDA abaixo do limiar a detecção de
 * barge-in tolera sem zerar o cronômetro.
 *
 * **Existe por um FAIL medido.** No G4 (18/09/2026, 5 sessões reais), quatro
 * repetições calaram entre 302 e 705 ms e **uma levou 3.057 ms** — o dobro do
 * teto, e depois ficou muda. O portão exige 5 de 5, então reprovou.
 *
 * ★ A causa está na frequência da medição: `avaliar` roda a cada quadro de
 * animação (~16 ms), e a versão anterior chamava `limpar()` **a cada quadro
 * abaixo do limiar**, recomeçando os 300 ms do zero. Fala humana tem quedas
 * naturais de energia — entre sílabas, e principalmente nas consoantes surdas
 * (s, f, t, p, x). Cada uma delas zerava a contagem. Os 3.057 ms foram isso:
 * o consultor falou por cima o tempo todo, e o cronômetro nunca fechou 300 ms
 * SEGUIDOS.
 *
 * 150 ms é mais longo que a pausa de uma consoante surda e mais curto que uma
 * pausa de intenção. Queda maior que isso volta a zerar — aí não é dip de
 * fala, é fim de fala.
 *
 * ⚠️ **[T109] Tentei subir para 400 ms e desfiz — a mudança não conserta o que
 * eu achava que consertava.** Medido em 19/09, mesmo código, mudando só a
 * frase: *"Qual a capital do Brasil?"* corta em **964 ms (5/5)**; *"Espera.
 * Qual a capital do Brasil?"* corta em **1.400 ms (12/20)**. Atribuí a
 * diferença a esta tolerância — errado. Quem segura é a guarda anti-tosse da
 * [T079] em `_aoCompletarHisterese`: aos 300 ms o consultor está **no meio da
 * pausa**, `_acima` é falso, e o disparo não sai. A contagem recomeça quando
 * ele retoma, e é daí que vêm os ~500 ms extras.
 *
 * A guarda está CERTA — ela é o que mantém uma tosse de 250 ms fora. Separar
 * *"Espera."* de uma tosse exigiria olhar o transcript, não o RMS, e isso é
 * outro projeto. Fica registrado para ninguém mexer nesta constante pelo
 * motivo errado, como eu mexi.
 */
export const TOLERANCIA_DE_QUEDA_NO_BARGE_IN_MS = 150

/**
 * VAD de auto-envio do Modo de Voz — dispara `aoLiberar()` depois de
 * `RELEASE_VAD_MS` de silêncio, SÓ quando a fala anterior se sustentou por
 * `MIN_FALA_MS`. Silêncio ANTES de completar o mínimo cancela o cronômetro
 * (evita que um ruído breve arme o release).
 *
 * ⚠️ Os DOIS cronômetros (armar por `MIN_FALA_MS` e liberar por
 * `RELEASE_VAD_MS`) são `setTimeout`, nunca `Date.now()` — o projeto roda
 * Jest 24 (fake timers LEGADOS, sem mock de `Date`); `Date.now()` correria em
 * tempo REAL enquanto `jest.advanceTimersByTime` só move os temporizadores,
 * e o VAD nunca armaria em teste algum. Mesma cautela do `RELEASE_VAD_MS`
 * original, que já era `setTimeout`.
 *
 * @param {{aoLiberar: Function}} opts
 * @returns {{avaliar: (rms: number) => void, prepararNovoTurno: Function, limpar: Function}}
 */
export const criarVadDeAutoEnvio = ({ aoLiberar }) => {
  let _falouOSuficiente = false
  let _timeoutDeArmar = null
  let _timeoutDeRelease = null

  const limparArmar = () => {
    if (_timeoutDeArmar) {
      clearTimeout(_timeoutDeArmar)
      _timeoutDeArmar = null
    }
  }

  const limparRelease = () => {
    if (_timeoutDeRelease) {
      clearTimeout(_timeoutDeRelease)
      _timeoutDeRelease = null
    }
  }

  const limpar = () => {
    limparArmar()
    limparRelease()
  }

  // Estado do VAD é por-turno — sem isto, o "falando"/cronômetro do turno
  // anterior vazaria pro turno novo.
  const prepararNovoTurno = () => {
    limpar()
    _falouOSuficiente = false
  }

  const avaliar = (rms) => {
    if (rms >= LIMIAR_VAD_RMS) {
      limparRelease()
      if (!_falouOSuficiente && !_timeoutDeArmar) {
        _timeoutDeArmar = setTimeout(() => {
          _timeoutDeArmar = null
          _falouOSuficiente = true
        }, MIN_FALA_MS)
      }
      return
    }
    // Silêncio antes de completar `MIN_FALA_MS`: cancela o armar — a fala
    // nunca chegou a se sustentar o suficiente.
    limparArmar()
    if (_falouOSuficiente && !_timeoutDeRelease) {
      _timeoutDeRelease = setTimeout(() => {
        _timeoutDeRelease = null
        _falouOSuficiente = false
        aoLiberar()
      }, RELEASE_VAD_MS)
    }
  }

  return { avaliar, prepararNovoTurno, limpar }
}

/**
 * VAD de barge-in — dispara `aoDetectarFala()` UMA vez quando o RMS fica
 * acima de `LIMIAR_BARGE_IN_RMS` por `HISTERESE_BARGE_IN_MS` seguidos.
 * Silêncio (ou queda abaixo do limiar) antes disso cancela o cronômetro —
 * um pico curto (tosse, clique) não deve interromper a IA.
 *
 * `setTimeout`, não `Date.now()` — mesma cautela de `criarVadDeAutoEnvio`
 * acima (Jest 24, fake timers legados).
 *
 * @param {{aoDetectarFala: Function}} opts
 * @returns {{avaliar: (rms: number) => void, resetar: Function}}
 */
export const criarVadDeBargeIn = ({ aoDetectarFala }) => {
  let _disparado = false
  let _timeout = null
  let _quedaTimeout = null
  // O último quadro ficou acima do limiar? É o que se confere no instante do
  // disparo — ver `_aoCompletarHisterese`.
  let _acima = false

  const limparQueda = () => {
    if (_quedaTimeout) {
      clearTimeout(_quedaTimeout)
      _quedaTimeout = null
    }
  }

  const limpar = () => {
    if (_timeout) {
      clearTimeout(_timeout)
      _timeout = null
    }
    limparQueda()
  }

  const resetar = () => {
    limpar()
    _disparado = false
    _acima = false
  }

  /**
   * ★ [T079] A GUARDA que mantém a tosse de fora.
   *
   * Tolerar quedas curtas (para os 3.057 ms do G4 não voltarem) abriria a
   * porta para um pico de 250 ms disparar: o cronômetro sobreviveria à queda
   * e fecharia os 300 ms sozinho. Conferir `_acima` AQUI fecha essa porta sem
   * reintroduzir o defeito — numa interrupção de verdade o consultor **ainda
   * está falando** aos 300 ms; numa tosse, não.
   *
   * Não `_disparado = true` quando não dispara: o cronômetro é rearmado no
   * próximo quadro acima do limiar, e a interrupção seguinte é medida limpa.
   */
  const _aoCompletarHisterese = () => {
    _timeout = null
    if (!_acima) return
    _disparado = true
    aoDetectarFala()
  }

  const avaliar = (rms) => {
    if (_disparado) return
    if (rms >= LIMIAR_BARGE_IN_RMS) {
      _acima = true
      // A fala voltou dentro da tolerância: a queda não conta.
      limparQueda()
      if (!_timeout) _timeout = setTimeout(_aoCompletarHisterese, HISTERESE_BARGE_IN_MS)
      return
    }
    _acima = false
    // ⚠️ Não zera aqui — era exatamente isto que fazia cada consoante surda
    // recomeçar os 300 ms. Só uma queda SUSTENTADA cancela a acumulação.
    if (_timeout && !_quedaTimeout) {
      _quedaTimeout = setTimeout(() => {
        _quedaTimeout = null
        limpar()
      }, TOLERANCIA_DE_QUEDA_NO_BARGE_IN_MS)
    }
  }

  return { avaliar, resetar }
}
